import { useCallback } from 'react';
import { checkModulePermissions } from '@web-config-app/core';
import type { ResolvedModule } from '@web-config-app/core';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useConfigAppPath } from '../use-config-app-path/use-config-app-path';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { useHasPermissionsHelpers } from '../use-has-permissions-helpers/use-has-permissions-helpers';

/**
 * Returns true when a module leads to a reachable destination the current
 * user can open. A module must both satisfy its own permissions AND have
 * a reachable leaf:
 *  - entity modules require a matching (permission-filtered) entityConfig
 *  - schema / component pages are reachable as leaves
 *  - containers recurse — reachable only if ≥ 1 descendant is, otherwise
 *    the user would land on ContainerIndexRedirect → ErrorPage
 */
const isAccessibleModule = (
  module: ResolvedModule,
  entityConfigs: Record<string, unknown>,
  hasAllPermissions: (permissions: string[]) => boolean,
): boolean => {
  if (!checkModulePermissions(module.permissions, hasAllPermissions)) {
    return false;
  }
  if (module.type === 'entity') return module.id in entityConfigs;
  if (module.type === 'schemaPage' || module.type === 'componentPage')
    return true;
  return (module.modules ?? []).some((child) =>
    isAccessibleModule(child, entityConfigs, hasAllPermissions),
  );
};

export const useAppRedirectPaths = () => {
  const {
    tenantId,
    modules = [],
    entityConfigs = {},
    permissionsFilterComplete,
  } = useConfigApp();
  const { baseAuthoringEnvironment } = useAuthoringEnvironments();
  const { hasAllPermissions } = useHasPermissionsHelpers({
    environmentKey: baseAuthoringEnvironment.environmentKey,
    tenantId,
    isAuthenticated: true,
  });
  const generateConfigPaths = useConfigAppPath();

  /**
   * Resolves the tenant-root landing path. Returns the first tab with
   * any accessible content so the tab's own index redirect
   * (`TabIndexRedirect` in `app-routes`) can pick the concrete
   * destination — e.g. a landing component page, an entity list, or
   * the first accessible non-entity child. Delegating ensures the
   * tenant root and a direct tab click land on the same place.
   */
  const landingRedirectPath = useCallback(() => {
    if (!permissionsFilterComplete) return null;

    const landingTab = modules.find((tab) =>
      isAccessibleModule(tab, entityConfigs, hasAllPermissions),
    );
    if (!landingTab) return null;

    const { tabPath } = generateConfigPaths({ tabId: landingTab.id });
    return tabPath;
  }, [
    modules,
    entityConfigs,
    hasAllPermissions,
    generateConfigPaths,
    permissionsFilterComplete,
  ]);

  return {
    landingRedirectPath,
  };
};
